import React from "react";

import * as FormRow from "./FormRow.js";
import * as Utils from "./Utils.js";
import * as Locales from "./locales";
import { MultiSelectList } from "./MultiSelectList";
import { SingleItemPage } from "./SingleItemPage";
import { PersonList } from "./PersonPage";
import { WorkingGroupList } from "./WorkingGroupPage";

const PageCacheName = Utils.PageNames.Organizations;
const PageApiPath = Utils.PagePaths.Organizations;
const PageName = PageCacheName;

export const FieldNames = {
   ShortName: "shortName",
   AccessGroupName: "accessGroupName",
   Overview: "overview",
   HomePageUrl: "homePageUrl",
   LogoUrl: "logoUrl",
   KeyVisualUrl: "keyVisualUrl",
   Classification: "classification",
   InlineDescription: "inlineDescription",
   Search: "search",
   SearchDescription: "searchDescription",
   Status: "status"
};

export const ChildListNames = {
   Contacts: "contacts",
   WorkingGroups: "workingGroups"
};

export const OrganizationStatus = {
   Proposed: 1,
   Approved: 2,
   Active: 3,
   Completed: 4,
   Inactive: 5,
   Invisible: 6
}

const FieldDefinitions = [
   {
      name: Utils.FieldNames.Name,
      defaultValue: "",
      viewControlType: FormRow.FieldType.Label,
      editControlType: FormRow.FieldType.TextInput,
      maxTextLength: 80
   },
   {
      name: FieldNames.ShortName,
      defaultValue: "",
      viewControlType: FormRow.FieldType.Label,
      editControlType: FormRow.FieldType.TextInput,
      maxTextLength: 40
   },
   {
      name: FieldNames.AccessGroupName,
      defaultValue: "",
      viewControlType: FormRow.FieldType.Label,
      editControlType: FormRow.FieldType.TextInput,
      maxTextLength: 80,
      editorOnly: true
   },
   {
      name: FieldNames.HomePageUrl,
      defaultValue: "https://opcfoundation.org/markets-collaboration/",
      viewControlType: FormRow.FieldType.Label,
      editControlType: FormRow.FieldType.TextInput,
      maxTextLength: 80
   },
   {
      name: FieldNames.LogoUrl,
      defaultValue: "",
      viewControlType: FormRow.FieldType.Label,
      editControlType: FormRow.FieldType.TextInput,
      maxTextLength: 80,
      editorOnly: true
   },
   {
      name: FieldNames.KeyVisualUrl,
      defaultValue: "",
      viewControlType: FormRow.FieldType.Label,
      editControlType: FormRow.FieldType.TextInput,
      maxTextLength: 80,
      editorOnly: true
   },
   {
      name: FieldNames.Classification,
      defaultValue: "",
      viewControlType: FormRow.FieldType.ChipArray,
      editControlType: FormRow.FieldType.TextInput,
      maxTextLength: 80
   },
   {
      name: Utils.FieldNames.Sort,
      defaultValue: 1,
      viewControlType: FormRow.FieldType.Label,
      editControlType: FormRow.FieldType.NumberInput,
      minValue: 1,
      maxValue: 10000,
      editorOnly: true
   },
   {
      name: FieldNames.Status,
      defaultValue: OrganizationStatus.Active,
      viewControlType: FormRow.FieldType.Label,
      editControlType: FormRow.FieldType.OptionSet
   },
   {
      name: FieldNames.Overview,
      defaultValue: "",
      viewControlType: FormRow.FieldType.BlockLabel,
      editControlType: FormRow.FieldType.TextArea,
      showLabel: true
   }
];

const FilterDefinitions = [
   {
      name: FieldNames.Search,
      defaultValue: "",
      editControlType: FormRow.FieldType.TextInput,
      maxTextLength: 40
   },
   {
      name: FieldNames.SearchDescription,
      defaultValue: false,
      editControlType: FormRow.FieldType.CheckBox
   }
];

const ChildDefinitions = [
   {
      name: ChildListNames.WorkingGroups,
      url: "/organization/workinggroups/",
      renderList: (props) => <WorkingGroupList {...props} useMultiSelect={true} />
   },
   {
      name: ChildListNames.Contacts,
      url: "/organization/contacts/",
      renderList: (props) => <PersonList {...props} useMultiSelect={true} />
   }
];

function renderFieldValue(context, name, item, field) {
   const value = FormRow.renderFieldValue(context, name, item, field, Utils.PagePaths.Organizations);
   if (value) {
      return value;
   }
   switch (name) {
      case Utils.FieldNames.Name: {
         return <FormRow.LinkField
            id={item.id}
            displayText={item.name}
            path={PageApiPath}
            noLink={context.noLink}
            parentPath={context.parentPath}
         />;
      }
      case FieldNames.HomePageUrl: {
         return <a
            className='m-0 p-0 nav-link'
            target='_blank'
            rel='noopener noreferrer'
            href={item[name]}
         >
            {item[name]}
         </a>;
      }
      case FieldNames.LogoUrl: {
         const url = item[name];
         if (url?.length) {
            return <img
               style={{ border: '2px solid white', background: 'white' }}
               src={url}
               height="46px"
               alt={item[Utils.FieldNames.Name]}
            />;
         }
         return url;
      }
      case FieldNames.KeyVisualUrl: {
         const url = item[name];
         if (url?.length) {
            return <img
               style={{ border: '2px solid white', background: 'white' }}
               src={url}
               height="46px"
               alt={item[Utils.FieldNames.Name]}
            />;
         }
         return url;
      }
      case FieldNames.InlineDescription: {
         return <label
            style={{ lineHeight: 1.2 }}
            className="pt-1 pb-0 pe-1 fluid w-100"
            dangerouslySetInnerHTML={{ __html: Utils.sanitizeString(item.description) }}>
         </label>
      }
      case FieldNames.Status: {
         return Utils.toString(OrganizationStatus, item.status, "workingGroupStatus");
      }
      default: {
         return item[name];
      }
   }
}

function toJson(item) {
   return {
      id: item.id,
      name: item.name,
      shortName: item.shortName,
      accessGroupName: item.accessGroupName,
      overview: item.overview,
      classification: item.classification,
      homePageUrl: item.homePageUrl,
      logoUrl: item.logoUrl,
      keyVisualUrl: item.keyVisualUrl,
      sort: Number(item.sort),
      lastUpdateTime: Utils.getDate(item.lastUpdateTime),
      status: item.status
   }
};

function initializeNewItem(props, newItem) {
   const {
      item
   } = props;
   if (item) newItem.status = OrganizationStatus.Proposed;
   if (item?.name) newItem.name = `(Copy) ${item.name}`;
   if (item?.shortName) newItem.shortName = `(Copy) ${item.shortName}`;
   if (item?.accessGroupName) newItem.accessGroupName = item.accessGroupName;
   if (item?.classification) newItem.classification = item.classification;
   if (item?.sort) newItem.sort = item.sort + 1;
}

function getEditorOptions(context, item) {
   const workingGroupStatuses = Object.keys(OrganizationStatus).map((ii) => {
      return {
         id: OrganizationStatus[ii],
         name: Locales.getDisplayText(`workingGroupStatus_${ii}`)
      };
   });
   return {
      [FieldNames.Status]: workingGroupStatuses
   };
};

function getEditorStates(context, item) {
   const states = {
      [FieldNames.Status]: FormRow.FieldState.Normal
   };
   if (item?.status < OrganizationStatus.Active) {
      return {
         ...states
      };
   }
   return states;
};

function updateInitialFilter(context, filters) {
   filters[FieldNames.Search] = "";
   filters.exclude = [
      Utils.FieldNames.ProfileGroupId,
      FieldNames.SearchDescription
   ];
};

function updateBeforeApplyFilters(filters, items) {
   if (!filters.searchDescription) {
      items.map(ii => {
         ii.search = `${ii.name} ${ii.shortName}`;
         return ii;
      });
   }
};

export const OrganizationList = (props) => {
  
   const {
      parent,
      url,
      containerName,
      storageKey
   } = props;

   const columns = [{ name: FieldNames.InlineDescription, width: 8 }];
   const sortColumn = { name: FieldNames.Name };

   return (
      <MultiSelectList
         {...props}
         pageName={PageName}
         containerName={containerName}
         storageKey={storageKey}
         parent={parent}
         url={url}
         pathApiUrl={PageApiPath}
         fieldDefinitions={FieldDefinitions}
         toJson={toJson}
         renderFieldValue={renderFieldValue}
         initializeNewItem={initializeNewItem}
         filterDefinitions={FilterDefinitions}
         updateInitialFilter={updateInitialFilter}
         updateBeforeApplyFilters={updateBeforeApplyFilters}
         columns={columns}
         sortColumn={sortColumn}
      />);
}

export const OrganizationPage = (props) => {

   const {
      parent,
      url,
      containerName,
      storageKey
   } = props;

   return (
      <SingleItemPage
         {...props}
         pageName={PageName}
         pathApiUrl={PageApiPath}
         containerName={containerName}
         storageKey={storageKey}
         parent={parent}
         url={url}
         fieldDefinitions={FieldDefinitions}
         toJson={toJson}
         childDefinitions={ChildDefinitions}
         renderFieldValue={renderFieldValue}
         getViewerStates={getEditorStates}
         getEditorStates={getEditorStates}
         getEditorOptions={getEditorOptions}
         initializeNewItem={initializeNewItem}
      />);
}
